import { totalItems, useCart } from '@agos/customer-core'
import { useEffect } from 'react'

import { flightFrom, pressTarget, type Press } from './fly-to-cart'

/**
 * The dot that flies from a pressed button to the basket — the moving half.
 *
 * The rules are in `fly-to-cart.ts` and tested there; this is the DOM. It renders nothing, mounts once
 * app-wide as a banner, and watches two things: the last pointer-down anywhere, and the basket's count.
 * When the count goes up shortly after a press, a dot travels from the press to whatever carries
 * `data-cart-icon`.
 *
 * Nothing calls it. That is the design and legacy's: wiring an animation into every add means every
 * future add has to remember, and the ones that forget are invisible. It also means the daily menu got
 * this for nothing.
 *
 * **It gives up quietly rather than half-working.** No cart icon on the page (the checkout hides it),
 * or a customer who asked for less motion — nothing happens, and nothing is logged. An animation is the
 * one feature where failing silently is the correct behaviour.
 */
export function FlyToCartLayer() {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined

    let press: Press | null = null
    let previous = totalItems(useCart.getState().lines)

    function onPointerDown(event: PointerEvent) {
      const target = pressTarget(event.target instanceof Element ? event.target : null)
      if (target === null) return
      const box = target.getBoundingClientRect()
      press = { x: box.left + box.width / 2, y: box.top + box.height / 2, at: Date.now() }
    }

    // Capture phase: a handler that stops propagation would otherwise hide the press from us, and the
    // press is only being read.
    document.addEventListener('pointerdown', onPointerDown, true)

    const unsubscribe = useCart.subscribe((state) => {
      const next = totalItems(state.lines)
      const from = flightFrom(press, previous, next, Date.now())
      previous = next
      if (from !== null) fly(from)
    })

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      unsubscribe()
    }
  }, [])

  return null
}

function fly(from: Press): void {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  const target = document.querySelector<HTMLElement>('[data-cart-icon]')
  if (target === null) return

  const box = target.getBoundingClientRect()
  const endX = box.left + box.width / 2
  const endY = box.top + box.height / 2

  const dot = document.createElement('div')
  dot.dataset.flyToCart = ''
  Object.assign(dot.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: '16px',
    height: '16px',
    borderRadius: '9999px',
    // The franchise's own colour, because `BrandingApplier` has already written it on `<html>`.
    background: 'hsl(var(--primary))',
    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.4)',
    zIndex: '9999',
    pointerEvents: 'none',
    opacity: '1',
    willChange: 'transform, opacity',
    transform: `translate(${from.x - 8}px, ${from.y - 8}px) scale(1)`,
    transition: 'transform 600ms cubic-bezier(0.5, 0.05, 0.4, 1), opacity 600ms ease-in',
  })
  document.body.appendChild(dot)

  // Flush layout so the browser has a start position to transition *from*; without it both styles are
  // applied in one frame and the dot simply appears at the basket.
  void dot.offsetWidth

  dot.style.transform = `translate(${endX - 8}px, ${endY - 8}px) scale(0.4)`
  dot.style.opacity = '0'

  // Both, because a `transitionend` that never fires — a backgrounded tab, an interrupted transition —
  // would leave the dot on the page for good.
  const remove = () => dot.remove()
  dot.addEventListener('transitionend', remove, { once: true })
  window.setTimeout(remove, 900)
}
